import { OrderDirection } from '@block-explorer/ui';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRouteMatchPath } from './utils';

export interface MatchProps {
  params: any;
  isExact: boolean;
  path: string;
  url: string;
}

export type LocationState =
  | Partial<{
      allowedGoBack: boolean;
      prevRouteMatch: Record<string, any>;
      activeTabIndexByKey: Record<string, number>;
      rowsPerPageByKey: Record<string, number>;
      activePageNumberByKey: Record<string, number>;
      activeOrderByKey: Record<string, string | undefined>;
      activeOrderDirectionByKey: Record<string, OrderDirection>;
    }>
  | undefined;

export const useCustomHistory = () => {
  const navigate = useNavigate<LocationState>();
  const location = useLocation();
  const match = useRouteMatchPath();

  // overwrite history push
  const push = (path: string, state?: Partial<LocationState>) => {
    // when navigation with allowedGoBack save current page match for back button label
    if (state?.allowedGoBack) {
      state.prevRouteMatch = match;
    }

    navigate(path, { state });
  };

  const goBack = () => navigate(-1);
  const replace = (path: string, state?: any) =>
    navigate(path, { replace: true, state });

  return { location, push, goBack, replace };
};

export const useCustomLocation = () => {
  const history = useCustomHistory();
  const location = history.location;

  const setState = (
    callback: (state: Partial<LocationState>) => Partial<LocationState>
  ) => {
    const newState = callback(location.state);
    history.replace(location.pathname, {
      ...location.state,
      ...newState,
    });
  };

  return { ...location, setState };
};

export const usePaginationState = (paginationStateKey: string) => {
  const { state, setState } = useCustomLocation();
  const currentPage = state?.activePageNumberByKey?.[paginationStateKey] ?? 1;
  const perPage = state?.rowsPerPageByKey?.[paginationStateKey] ?? 10;
  const orderBy = state?.activeOrderByKey?.[paginationStateKey] ?? undefined;
  const orderDirection = orderBy
    ? state?.activeOrderDirectionByKey?.[paginationStateKey]
    : undefined;

  const setCurrentPage = (page: number) => {
    setState((state) => ({
      ...state,
      activePageNumberByKey: {
        ...state?.activePageNumberByKey,
        [paginationStateKey]: page,
      },
    }));
  };

  const setPerPage = (pageLimit: number) => {
    setState((state) => ({
      ...state,
      activePageNumberByKey: {
        [paginationStateKey]: 1,
      },
      rowsPerPageByKey: {
        ...state?.rowsPerPageByKey,
        [paginationStateKey]: pageLimit,
      },
    }));
  };

  const setOrder = (orderBy: string | undefined, direction: OrderDirection) => {
    setState((state) => ({
      ...state,
      activeOrderByKey: {
        ...state?.activeOrderByKey,
        [paginationStateKey]: orderBy,
      },
      activeOrderDirectionByKey: {
        ...state?.activeOrderDirectionByKey,
        [paginationStateKey]: direction,
      },
    }));
  };

  return {
    currentPage: currentPage,
    perPage: perPage,
    setCurrentPage: setCurrentPage,
    setPerPage: setPerPage,
    orderBy,
    orderDirection,
    setOrder: setOrder,
  };
};

export const useTabsState = (tabsStateKey: string) => {
  const { state, setState } = useCustomLocation();
  const activeIndex = state?.activeTabIndexByKey?.[tabsStateKey] ?? 0;

  const isTabActive = (index: number) => {
    return index === activeIndex;
  };

  const setTabActive = (index: number, page?: number) => {
    setState((state) => ({
      ...state,
      activeTabIndexByKey: {
        ...state?.activeTabIndexByKey,
        [tabsStateKey]: index,
      },
      activePageNumberByKey:
        page == null
          ? state?.activePageNumberByKey
          : {
              ...state?.activePageNumberByKey,
              [tabsStateKey]: page,
            },
    }));
  };

  return {
    isTabActive,
    setTabActive,
  };
};
